import React, { useState, useEffect } from 'react';
import { getAssetPath } from '../utils/assetHelper';
import WalletOverviewChart from '../components/charts/WalletOverviewChart';
import { useUser } from '../context/UserContext';
import './HomePage.scss';

// Add Bootstrap type definition
declare global {
  interface Window {
    bootstrap: any;
  }
}

const HomePage = () => {
  const [chartPeriod, setChartPeriod] = useState('7');
  const { userProfile, characters, isLoading } = useUser();
  const currentCharacter = characters && characters.length > 0 ? characters[0] : null;

  // Get avatar image based on character type
  const getAvatarImage = () => {
    if (!currentCharacter) return 'assets/images/avatar-1.png';
    
    switch (currentCharacter.type) {
      case 'Phantom':
        return 'assets/images/avatar-2.png';
      case 'Dark Cleric':
        return 'assets/images/character-2.png';
      default:
        return 'assets/images/avatar-1.png';
    }
  };

  useEffect(() => {
    // Initialize any needed scripts or plugins for this page
    document.title = 'Dashboard - Phantom Clash';
    
    // Initialize tooltips
    if (typeof window !== 'undefined' && window.bootstrap) {
      const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
      tooltipTriggerList.map(function (tooltipTriggerEl) {
        return new window.bootstrap.Tooltip(tooltipTriggerEl);
      });
    }
  }, []);

  if (isLoading) {
    return <div className="loading-indicator">Loading user data...</div>;
  }

  return (
    <>
      {/* Welcome */}
      <div className="p-user-welcome">
        <div className="p-user-welcome__avatar">
          <img src={getAssetPath(getAvatarImage())} alt="Avatar" />
          <span></span>
          <video src={getAssetPath("assets/images/s-m-bg.mp4")} autoPlay loop muted playsInline></video>
        </div>
        <div className="p-user-welcome__text">
          <h1>
            <span>Hello</span>, {currentCharacter?.name || 'Adventurer'} 
            {currentCharacter && (
              <small><span>{currentCharacter.type}</span></small>
            )}
          </h1>
          <p>Ready to reclaim your power and conquer the gods?</p>
        </div>
        <div className="ms-auto box">
          <a href="#" className="c-i-button me-1" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-title="Edit">
            <i className="bi bi-pencil-square"></i>
          </a>
          <a href="#" className="c-i-button" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-title="Achievements and progress">
            <i className="bi bi-award-fill"></i>
            <span className="c-i-button__stats"></span>
          </a>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="p-quick-stats box">
        <div className="p-quick-stats__item">
          <div className="p-quick-stats__item-count">
            <div className="p-quick-stats__item-icon">
              <img src={getAssetPath("assets/images/phan-icon@1x.png")} alt="" />
              <img src={getAssetPath("assets/images/phan-icon@1x.png")} alt="" />
            </div>
            <span>{userProfile?.valorPoints || 0}<small></small></span>
          </div>
          <small>$PHAN balance</small>
          <a href="#" className="p-quick-stats__expand"><i className="bi bi-arrows-angle-expand"></i></a>
        </div>
        <div className="p-quick-stats__item">
          <div className="p-quick-stats__item-count">
            <div className="p-quick-stats__item-icon">
              <img src={getAssetPath("assets/images/valor-points@1x.png")} alt="" />
              <img src={getAssetPath("assets/images/valor-points@1x.png")} alt="" />
            </div>
            <span>{userProfile?.valorPoints || 0}</span>
          </div>
          <small>Total Valor Points</small>
          <a href="#" className="p-quick-stats__expand"><i className="bi bi-arrows-angle-expand"></i></a>
        </div>
        <div className="p-quick-stats__item">
          <div className="p-quick-stats__item-count">
            <span className="d-flex align-items-center">
              {currentCharacter?.level || 1} <small className="lvl">lvl</small>
            </span>
          </div>
          <small>{currentCharacter?.name || 'New Character'}</small>
        </div>
      </div>

      {/* Wallet Overview */}
      <div className="p-wallet-overview">
        <div className="c-section-title">
          <h1>
            <img src={getAssetPath("assets/images/wallet-overview.svg")} alt="" />
            Wallet Overview
          </h1>
          <div className="p-wallet-overview__actions box ms-auto">
            <div className="p-wallet-overview__actions-balance me-1">
              <div className="p-wallet-overview__actions-balance-img">
                <img src={getAssetPath("assets/images/phan-icon@1x.png")} alt="" />
                <img src={getAssetPath("assets/images/phan-icon@1x.png")} alt="" />
              </div>
              <div className="p-wallet-overview__actions-balance-count">{userProfile?.valorPoints || 0}<small></small></div>
              <div className="p-wallet-overview__actions-balance-symbol">$PHAN</div>
            </div>
            <a href="#" className="p-wallet-overview__actions-swap me-1">
              <i className="bi bi-arrow-left-right"></i>
              Swap <small>SOL</small> for <small className="blue">$PHAN</small>
            </a>
            <a href="#" className="c-i-button" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-title="Transaction History">
              <i className="bi bi-clock-history"></i>
            </a>
          </div>
        </div>
        <div className="p-wallet-overview__chart">
          <select id="timeRange" className="c-selectbox" value={chartPeriod} onChange={(e) => setChartPeriod(e.target.value)} aria-label="Chart time period">
            <option value="7">7 Days</option>
            <option value="30">30 Days</option>
          </select>
          <WalletOverviewChart period={chartPeriod} />
        </div>
      </div>

      {/* Adventure Stats */}
      <div className="p-adventure-stats">
        <div className="c-section-title">
          <h1>
            <img src={getAssetPath("assets/images/adventure-stats.svg")} alt="" />
            Your Adventure Stats
          </h1>
          <div className="box ms-lg-auto ms-0 mt-lg-0 mt-2">
            <a href="#" className="c-r-button c-r-button--black me-1">
              <i className="bi bi-bar-chart-fill"></i>
              View Full Stats
            </a>
            <a href="#" className="c-r-button">
              <img src={getAssetPath("assets/images/sword.svg")} alt="" />
              Join a Challenge
            </a>
          </div>
        </div>
        <div className="p-adventure-stats__list box">
          <div className="p-adventure-stats__item">
            <div className="p-adventure-stats__item-icon">
              {currentCharacter?.level || 1} <small>LVL</small>
            </div>
            <div className="p-adventure-stats__item-text">
              <h5>{currentCharacter?.name || 'New Character'}</h5>
              <div className="c-progress">
                <div className="c-progress__bar" style={{ width: `${(currentCharacter?.experience || 0) % 100}%` }}></div>
                <div className="c-progress__count"><span>{currentCharacter ? currentCharacter.level + 1 : 2}</span></div>
              </div>
            </div>
            <div className="p-adventure-stats__item-light"></div>
          </div>
          <div className="p-adventure-stats__item blue">
            <div className="p-adventure-stats__item-icon">
              <img src={getAssetPath("assets/images/shield.png")} alt="" />
            </div>
            <div className="p-adventure-stats__item-text">
              <h5 className="mb-0">Latest Raid Completed</h5>
              <span className="defeat">Victory</span>
              <small className="date">
                {currentCharacter?.lastPlayed 
                  ? new Date(currentCharacter.lastPlayed).toLocaleDateString() 
                  : 'No raids yet'}
              </small>
            </div>
            <div className="p-adventure-stats__item-light"></div>
          </div>
          <div className="p-adventure-stats__item dark-blue">
            <div className="p-adventure-stats__item-icon">
              {currentCharacter?.strength || 10} <small>STR</small>
            </div>
            <div className="p-adventure-stats__item-text">
              <h5 className="mb-3">Power Level</h5>
              <div className="c-progress dark-blue">
                <div className="c-progress__bar" style={{ width: '40%' }}></div>
                <div className="c-progress__count"><span>{currentCharacter ? currentCharacter.strength + 1 : 11}</span></div>
              </div>
            </div>
            <div className="p-adventure-stats__item-light"></div>
          </div>
        </div>
      </div>

      {/* Store Banner */}
      <div className="p-store-banner">
        <div className="c-section-title">
          <h1>
            <img src={getAssetPath("assets/images/store.svg")} alt="" />
            Marketplace and Phantom Store Access
          </h1>
        </div>
        <div className="box w-100">
          <div className="p-store-banner__content">
            <div className="p-store-banner__wrap">
              <h4>Phantom <img src={getAssetPath("assets/images/packagebox.svg")} alt="" /><span>Store</span></h4>
              <h1>Exclusive items <span>& upgrades <br />await!</span></h1>
              <div className="d-md-flex d-block w-100 align-items-center mb-4">
                <a href="#" className="c-button"><i className="bi bi-gem"></i> Browse Store</a>
                <a href="#" className="c-button c-button--bordered ms-md-auto mt-md-0 mt-2"><i className="bi bi-cart2"></i> Check Your Cart</a>
              </div>
              <div className="p-store-banner__offer">
                <img src={getAssetPath("assets/images/offer-icon@1x.png")} alt="" />
                <div className="p-store-banner__offer-text">
                  <h3>Special Offer</h3>
                  <p>Void Tempest Scythe</p>
                </div>
                <div className="p-store-banner__offer-badge">
                  <span>20% off!</span>
                  <i className="bi bi-chevron-right"></i>
                </div>
                <div className="p-store-banner__offer-pattern"></div>
              </div>
            </div>
            <div className="p-store-banner__pattern"></div>
            <video src={getAssetPath("assets/images/store-banner.mp4")} autoPlay loop muted playsInline></video>
            <div className="p-store-banner__s-pattern"></div>
            <div className="p-store-banner__bg"></div>
          </div>
        </div>
      </div>

      {/* Achievements */}
      <div className="p-achievements">
        <div className="c-section-title">
          <h1>
            <img src={getAssetPath("assets/images/medal.svg")} alt="" />
            Achievements and Milestones
          </h1>
          <div className="box ms-auto d-lg-flex d-none">
            <a href="#" className="c-r-button">
              <i className="bi bi-trophy"></i>
              View All Achievements
            </a>
          </div>
        </div>
        <div className="p-achievements__list box">
          <div className="p-achievements__item unlocked">
            <div className="p-achievements__item-icon">
              <img src={getAssetPath("assets/images/achievements-unlocked@1x.png")} alt="" />
            </div>
            <small>Achievement</small>
            <h1>Godslayer</h1>
            <div className="p-achievements__item-badge"><i className="bi bi-check-circle-fill"></i> Unlocked</div>
            <img src={getAssetPath("assets/images/achievements-bg@1x.png")} className="p-achievements__item-bg" alt="" />
            <div className="p-achievements__item-light"></div>
          </div>
          <div className="p-achievements__item unlocked">
            <div className="p-achievements__item-icon">
              <img src={getAssetPath("assets/images/achievements-unlocked@1x.png")} alt="" />
            </div>
            <small>Achievement</small>
            <h1>Godslayer</h1>
            <div className="p-achievements__item-badge"><i className="bi bi-check-circle-fill"></i> Unlocked</div>
            <img src={getAssetPath("assets/images/achievements-bg@1x.png")} className="p-achievements__item-bg" alt="" />
            <div className="p-achievements__item-light"></div>
          </div>
          <div className="p-achievements__item">
            <div className="p-achievements__item-icon">
              <img src={getAssetPath("assets/images/achievements@1x.png")} alt="" />
            </div>
            <small>Achievement</small>
            <h1>Godslayer</h1>
            <div className="c-progress" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-title="Reach Level 30">
              <div className="c-progress__bar" style={{ width: '40%' }}></div>
              <div className="c-progress__count"><span>25%</span></div>
            </div>
          </div>
          <div className="p-achievements__item">
            <div className="p-achievements__item-icon">
              <img src={getAssetPath("assets/images/achievements@1x.png")} alt="" />
            </div>
            <small>Achievement</small>
            <h1>Godslayer</h1>
            <div className="c-progress" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-title="Reach Level 30">
              <div className="c-progress__bar" style={{ width: '40%' }}></div>
              <div className="c-progress__count"><span>25%</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* Community Hub */}
      <div className="p-community-hub">
        <div className="c-section-title">
          <h1>
            <img src={getAssetPath("assets/images/community-hub.svg")} alt="" />
            Community Hub
          </h1>
        </div>
        <div className="box">
          <div className="p-community-hub__banner">
            <img src={getAssetPath("assets/images/separator.svg")} className="p-community-hub__banner-icon" alt="" />
            <h2>Connect with fellow</h2>
            <h1>Adventurers</h1>
            <ul className="p-community-hub__banner-buttons">
              <li>
                <a href="#" className="c-button">Follow us on Twitter</a>
                <span><i className="bi bi-twitter-x"></i> for updates!</span>
              </li>
              <li>
                <a href="#" className="c-button">Join our Discord</a>
                <span><i className="bi bi-discord"></i> for exclusive events</span>
              </li>
            </ul>
            <h4>Compete in the <span>'Battle of the Realms'</span> challenge</h4>
            <div className="p-community-hub__banner-mask"></div>
            <div className="p-community-hub__banner-ritual">
              <img src={getAssetPath("assets/images/community-hub-banner@1x.png")} alt="" />
            </div>
            <video src={getAssetPath("assets/images/community-hub.mp4")} autoPlay loop muted playsInline></video>
          </div>
        </div>
      </div>

      {/* News */}
      <div className="p-news">
        <div className="c-section-title">
          <h1>
            <img src={getAssetPath("assets/images/news.svg")} alt="" />
            Phantom Clash News
          </h1>
        </div>
        <div className="p-news__content">
          <div className="p-news__item">
            <div className="p-news__item-image">
              <img src={getAssetPath("assets/images/news-image-3.jpg")} alt="" />
            </div>
            <div className="p-news__item-text">
              <h1>Upcoming Patch: New Co-op Mode — Descent of the Realms!</h1>
              <div className="p-news__item-footer">
                <span><i className="bi bi-calendar4"></i> 15 October, at 11:00 AM</span>
                <a href="#" className="c-button c-button--blue-bordered">Read more</a>
              </div>
            </div>
          </div>
          <div className="p-news__item">
            <div className="p-news__item-image">
              <img src={getAssetPath("assets/images/news-image-1.jpg")} alt="" />
            </div>
            <div className="p-news__item-text">
              <h1>$PHAN Token Update: New Exchange Partnership Announced</h1>
              <div className="p-news__item-footer">
                <span><i className="bi bi-calendar4"></i> 15 October, at 11:00 AM</span>
                <a href="#" className="c-button c-button--blue-bordered">Read more</a>
              </div>
            </div>
          </div>
          <div className="p-news__item">
            <div className="p-news__item-image">
              <img src={getAssetPath("assets/images/news-image-2.jpg")} alt="" />
            </div>
            <div className="p-news__item-text">
              <h1>Dev Blog: Character Development Insights</h1>
              <div className="p-news__item-footer">
                <span><i className="bi bi-calendar4"></i> 15 October, at 11:00 AM</span>
                <a href="#" className="c-button c-button--blue-bordered">Read more</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="c-separator"></div>

      {/* Support Center */}
      <div className="p-support-center">
        <div className="p-support-center__mini-title">
          <img src={getAssetPath("assets/images/help.svg")} alt="" />
          <h4>Support Center</h4>
        </div>
        <h1>Having issues?</h1>
        <h1>Browse our <img src={getAssetPath("assets/images/book.svg")} alt="" /> <span>FAQs</span></h1>
        <h6>or contact support</h6>
        <div className="p-support-center__footer">
          <a href="#" className="c-button c-button--blue-bordered">Write to us</a>
          <a href="#" className="p-support-center__footer-link"><i className="bi bi-book"></i> FAQs</a>
        </div>
        <div className="p-support-center__pattern"></div>
        <img src={getAssetPath("assets/images/support-center-bg.png")} className="p-support-center__bg" alt="" />
        <video src={getAssetPath("assets/images/flash-2.mp4")} autoPlay loop muted playsInline></video>
      </div>
    </>
  );
};

export default HomePage; 